import { SagaIterator } from 'redux-saga';
import { all, put, call, takeEvery, PutEffect } from 'redux-saga/effects';
import { Action } from 'redux-actions';
import {
  deleteStatus,
  submitStatus,
  submitStatusSuccess,
  submitStatusFailure,
  SubmitStatusPayload,
  DeleteStatusPayload,
} from '../actions';
import { isApiError, requestNapi, requestPuppy } from '@wix/da-http-client';
import { normalize } from '@wix/da-shared-react/pkg/redux/normalizr';
import { putEntities } from '@wix/da-shared-react/pkg/redux/entities/actions';
import { deviationSchema } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/deviation';
import ddt from '@wix/da-ddt';
import {
  PapiDaSharedStatusPublish,
  PapiDeviation,
  PapiRequestDaSharedStatusPublish,
} from '@wix/da-papi-types';
import { getDeviationUrl } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { editDeviationSuccess } from '@wix/da-shared-react/pkg/redux/deviations/actionCreators';
import { logBiEvent } from '@wix/da-shared-react/pkg/biLogger/redux/saga';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import { PureBiData, SubmitContentBiEvent } from '@wix/da-bi/pkg/events';

export function fakeResponse(options): any {
  if (process.env.NODE_ENV !== 'production') {
    if (window['doError']) {
      return {
        error: 'invalid_request',
        error_description: 'Not logged in.',
        status: 'error',
      };
    }
    ddt.log('statusupdate', 'faking the response!');
    const { deviationsByType } = require('@wix/da-sample-data'); // eslint-disable-line global-require
    const deviation = deviationsByType.statuses[0];
    if (options.url === '/status/create' || options.url === '/status/update') {
      deviation.status.textContent = {
        excerpt: options.data.editorSnippet,
        html: {
          type: 'tiptap',
          markup: JSON.parse(options.data.editorRaw),
          features: JSON.parse(options.data.editorFeatures),
        },
      };
    }
    return { deviation };
  }
}

function* submitStatusSaga(action: Action<SubmitStatusPayload>): SagaIterator {
  const {
    editorRawState,
    statusId: statusid,
    backgroundProps = {},
    refreshAfterSubmit,
    visibility,
  } = action.payload as SubmitStatusPayload;
  ddt.log('statusupdate', 'payload', action.payload);
  const isEdit = statusid !== undefined;

  const { backgroundType, gradient, imageDisplayMode, imageTint, pattern, imageDeviation } =
    backgroundProps;
  const snakeCaseBackgroundParams = {
    background_type: backgroundType,
    image_tint: imageTint,
    image_deviationid: imageDeviation?.deviationId,
    image_display_mode: imageDisplayMode,
    gradient: gradient,
    pattern: pattern,
  };

  const editorParams = {} as any;
  try {
    editorParams.editorRaw = JSON.stringify(editorRawState);
  } catch (e) {}

  const data = {
    ...snakeCaseBackgroundParams,
    ...editorParams,
    statusid,
  };
  const method = 'post';
  const url = isEdit ? '/status/update' : '/status/create';
  ddt.log('statusupdate', 'request', { ...data, url });

  const result = yield call(
    requestNapi,
    {
      method,
      url,
      data,
    },
    fakeResponse
  );

  ddt.log('statusupdate', 'response', result);

  if (isApiError(result)) {
    if (result && result.errorCode === 302) {
      window.location.replace(result?.errorDetails?.redirectUrl ?? '');
    }
    yield put(submitStatusFailure({ error: result?.errorDescription }));
    ddt.log('statusupdate', 'failure', result);
    return;
  }

  // After save we have to publish it
  const publishData: PapiRequestDaSharedStatusPublish = {
    statusid: result.deviation.deviationId,
    tiers: visibility?.type === 'tier' ? visibility.tiers.map(tier => tier.deviationId) : undefined,
  } as any;
  const publishResult: PapiDaSharedStatusPublish = yield call(
    requestPuppy,
    {
      method: 'post',
      url: '/status/publish',
      data: publishData,
    },
    fakeResponse
  );
  ddt.log('statusupdate', 'response publish', publishResult);

  if (isApiError(publishResult)) {
    if (publishResult.errorCode === 302 && publishResult.errorDetails?.redirectUrl) {
      window.location.replace(publishResult.errorDetails.redirectUrl);
    }
    yield put(submitStatusFailure({ error: publishResult?.errorDescription }));
    ddt.log('statusupdate', 'failure', publishResult);
  }

  const resultActions: PutEffect<any>[] = [
    put(submitStatusSuccess({ statusId: publishResult.deviation.deviationId })),
  ];

  if (publishResult.deviation) {
    const { entities } = normalize(publishResult.deviation, deviationSchema);
    resultActions.push(put(putEntities({ entities })));
  }

  if (!isEdit) {
    yield call(
      logBiEvent,
      PureBiData<SubmitContentBiEvent>({
        evid: 11,
        group_exclusive: false,
        itemid: publishResult.deviation.deviationId,
        typeid: ResourceType.STATUS_DEVIATION,
      })
    );
  }

  if (isEdit) {
    resultActions.push(put(editDeviationSuccess({ deviation: publishResult.deviation })));
  }

  yield all(resultActions);
  ddt.log('statusupdate', 'success');
  // redirect
  if (refreshAfterSubmit) {
    window.location.reload();
  } else {
    const deviation: PapiDeviation = publishResult.deviation;
    window.location.assign(getDeviationUrl(deviation));
  }
}

function* deleteStatusSaga(action: Action<DeleteStatusPayload>): SagaIterator {
  const { statusId: statusid } = action.payload as DeleteStatusPayload;
  ddt.log('statusupdate', 'deleting', statusid);
  const result = yield call(
    requestPuppy,
    {
      method: 'post',
      url: `/status/delete`,
      data: { statusid },
    },
    fakeResponse
  );
  ddt.log('statusupdate', 'response', result);
}

export default function* saga(): any {
  yield all([takeEvery(submitStatus, submitStatusSaga), takeEvery(deleteStatus, deleteStatusSaga)]);
}
